import { build_stage_timeline, build_summary_rows, resolve_gate_id_from_stage_label } from "@/services/onboarding_journey_service";
import {
    build_binding_options,
    build_case_detail_checks,
    build_load_promises,
    get_case_forms
} from "@/views/onboarding_system_views/Gates/Verification/manage_verification_load_helpers";
import {
    apply_case_manual_binding_fallback,
    build_initial_case_checks,
    build_manual_mapping_by_link,
    check_has_manual_mappings,
    enrich_manual_checks_with_run_mappings
} from "@/views/onboarding_system_views/Gates/Verification/manage_verification_manual_runtime_helpers";
import {
    build_field_run_by_question,
    decorate_review_form_section,
    get_manual_outcome_action,
    get_manual_outcome_label,
    get_manual_outcome_tone
} from "@/views/onboarding_system_views/Gates/Verification/manage_verification_manual_helpers";
import { get_case_entity_type_id } from "@/views/onboarding_system_views/Gates/Verification/manage_verification_helpers";
import { manageVerificationResultMethods } from "@/views/onboarding_system_views/Gates/Verification/manage_verification_result_methods";
import { manageVerificationSupportMethods } from "@/views/onboarding_system_views/Gates/Verification/manage_verification_support_methods";
import {
    build_historical_manual_item,
    build_historical_manual_review_sections,
    get_manual_field_runs
} from "@/views/onboarding_system_views/Gates/Verification/historical_manual_context_helpers";
import {
    build_historical_risk_choice_rows,
    build_historical_risk_weight_lookup,
    get_first_text,
    get_historical_risk_choice_rows,
    get_historical_risk_fallback_items,
    get_historical_risk_max_score,
    get_historical_risk_question_definitions,
    get_historical_risk_weight_max,
    normalize_answer_values,
    resolve_historical_risk_assessment
} from "./esigning_setup_methods_utils";
import { build_historical_questionnaire_answers } from "./esigning_setup_request_summary_utils";
import {
    fetch_assigned_questions_to_assessment,
    fetch_risk_checklist_weights,
    fetch_submitted_risk_assessment
} from "@/services/onboarding_risk_service";

function reset_historical_gate_state(vm) {
    vm.historical_gate_checks_by_gate = {};
    vm.historical_risk_questions = [];
    vm.historical_risk_weights = [];
    vm.historical_risk_submitted = null;
    vm.historical_risk_assessment_name = "";
}

function build_effective_manual_checks(case_item, forms, fetched_data, manual_mapping_by_link) {
    const checks = build_case_detail_checks(case_item, forms, fetched_data, manual_mapping_by_link);
    return enrich_manual_checks_with_run_mappings(checks).then(async (enriched_checks) => {
        const runtime_manual_checks = await build_initial_case_checks([case_item], true);
        const enriched_runtime_manual_checks = await enrich_manual_checks_with_run_mappings(runtime_manual_checks);
        return enriched_checks.some(check_has_manual_mappings)
            ? enriched_checks
            : [
                ...enriched_checks,
                ...enriched_runtime_manual_checks.filter((check) => check?.verification_mode === "manual")
            ];
    });
}

function build_checks_by_gate(historical_gate_tabs = [], checks = [], effective_manual_checks = []) {
    const next_checks_by_gate = {};
    historical_gate_tabs.forEach((tab) => {
        if (tab.gate_id === "manual-verification") {
            next_checks_by_gate[tab.gate_id] = effective_manual_checks.filter((check) => check?.verification_mode === "manual");
            return;
        }
        if (tab.gate_id === "verification") {
            next_checks_by_gate[tab.gate_id] = checks.filter((check) => check?.verification_mode !== "manual" && !check?.is_gate_placeholder);
            return;
        }
        next_checks_by_gate[tab.gate_id] = [];
    });
    return next_checks_by_gate;
}

function get_gate_transition_log(transition_history = [], gate_id = "") {
    const normalized_gate_id = String(gate_id || "").trim().toLowerCase();
    if (!normalized_gate_id) return null;

    const logs = Array.isArray(transition_history) ? [...transition_history] : [];
    return logs
        .filter((log) => resolve_gate_id_from_stage_label(log?.to_status?.name || "") === normalized_gate_id)
        .sort((left, right) => new Date(right?.date_created || 0).getTime() - new Date(left?.date_created || 0).getTime())[0] || null;
}

async function load_historical_risk_context(vm, case_item = {}) {
    const has_risk_history_tab = vm.historical_gate_tabs.some((tab) => tab.gate_id === "risk-review");
    if (!has_risk_history_tab) return;

    const historical_risk_assessment = resolve_historical_risk_assessment(case_item);
    const entity_id = Number(case_item?.entity_profile_id || 0);
    if (!historical_risk_assessment?.id || !entity_id) return;

    const [questions, weights, submitted] = await Promise.all([
        fetch_assigned_questions_to_assessment(historical_risk_assessment.id).catch(() => []),
        fetch_risk_checklist_weights(historical_risk_assessment.id).catch(() => []),
        fetch_submitted_risk_assessment({ entity_id, assessment_id: historical_risk_assessment.id }).catch(() => null)
    ]);
    vm.historical_risk_questions = Array.isArray(questions) ? questions : [];
    vm.historical_risk_weights = Array.isArray(weights) ? weights : [];
    vm.historical_risk_submitted = submitted || case_item?.risk_result || null;
    vm.historical_risk_assessment_name = historical_risk_assessment.name;
}

export const esigning_setup_history_methods = {
    get_historical_gate_summary_rows(tab = this.active_historical_gate_tab) {
        if (!this.selected_case || !tab) return [];
        return build_summary_rows({ ...this.selected_case, workflow_display_stage: tab.label, current_workflow_status: tab.label, stage: tab.label, detail: tab.label, activation: "Completed" });
    },
    get_previous_historical_gate(tab = this.active_historical_gate_tab) {
        if (!tab) return null;
        const current_index = this.historical_gate_tabs.findIndex((item) => item.id === tab.id);
        return current_index > 0 ? this.historical_gate_tabs[current_index - 1] || null : null;
    },
    historical_gate_context_copy(tab = this.active_historical_gate_tab) {
        if (!tab) return "Read-only context carried forward from the completed gate.";
        const previous_gate = this.get_previous_historical_gate(tab);
        return previous_gate?.label
            ? `Reached after ${previous_gate.label}. Read-only context carried forward from the completed gate.`
            : "Read-only context carried forward from the completed gate.";
    },
    get_historical_gate_stage_timeline() {
        return this.selected_case ? build_stage_timeline(this.selected_case) : [];
    },
    active_historical_transition_log() {
        return get_gate_transition_log(this.workflow_transition_history, this.active_historical_gate_tab?.gate_id);
    },
    historical_gate_action_label() {
        return get_first_text([
            this.active_historical_transition_log()?.action_taken,
            this.active_historical_transition_log()?.to_status?.name
        ], "Completed");
    },
    historical_gate_status_label() {
        return get_first_text([
            this.active_historical_transition_log()?.to_status?.name,
            this.active_historical_transition_log()?.action_taken
        ], "Completed");
    },
    historical_gate_status_tone() {
        const label = String(this.historical_gate_status_label() || "").trim().toLowerCase();
        if (label.includes("reject")) return "danger";
        if (label.includes("request") || label.includes("change")) return "warning";
        if (label.includes("approve") || label.includes("complete")) return "success";
        return "secondary";
    },
    historical_gate_actor() {
        return get_first_text([
            this.active_historical_transition_log()?.reviewer,
            this.active_historical_transition_log()?.role
        ], "System");
    },
    historical_gate_completed_at() {
        return get_first_text([
            this.active_historical_transition_log()?.date_created
        ], "-");
    },
    historical_gate_reason() {
        return get_first_text([
            this.active_historical_transition_log()?.note
        ], "No reason was recorded for this gate.");
    },
    active_historical_manual_item() {
        return this.active_historical_gate_tab?.gate_id !== "manual-verification"
            ? null
            : build_historical_manual_item(this.active_historical_gate_checks.filter((check) => check?.verification_mode === "manual"));
    },
    historical_manual_status_label() {
        const manual_item = this.active_historical_manual_item();
        return get_first_text([get_manual_outcome_label(manual_item), manual_item?.manual_run?.status, manual_item?.status], "Completed");
    },
    historical_manual_status_tone() {
        const manual_item = this.active_historical_manual_item();
        return manual_item ? get_manual_outcome_tone(manual_item) || manual_item?.manual_run?.status_tone || manual_item?.status_tone || "secondary" : "secondary";
    },
    historical_manual_reviewer() {
        const manual_item = this.active_historical_manual_item();
        return get_first_text([manual_item?.manual_run?.reviewer_name, manual_item?.manual_run?.reviewer, manual_item?.raw_status?.reviewer_name, manual_item?.raw_status?.reviewer, manual_item?.raw_status?.updated_by_name], "Reviewed");
    },
    historical_manual_timestamp() {
        const manual_item = this.active_historical_manual_item();
        return get_first_text([manual_item?.last_run, manual_item?.manual_run?.completed_at, manual_item?.manual_run?.started_at], "-");
    },
    historical_manual_comment() {
        const manual_item = this.active_historical_manual_item();
        return get_first_text([manual_item?.manual_run?.decision_reason, manual_item?.raw_status?.decision_reason, manual_item?.raw_status?.reviewer_comment], "No reviewer comments were recorded for this gate.");
    },
    historical_manual_field_runs() {
        const manual_item = this.active_historical_manual_item();
        return manual_item ? get_manual_field_runs(manual_item) : [];
    },
    historical_manual_decision_action() {
        return get_manual_outcome_action(this.active_historical_manual_item());
    },
    historical_manual_decision_label() {
        const action = this.historical_manual_decision_action();
        if (action === "approve") return "Approved";
        if (action === "request_changes") return "Changes requested";
        if (action === "reject") return "Rejected";
        return this.historical_manual_status_label();
    },
    historical_manual_decision_tone() {
        const action = this.historical_manual_decision_action();
        if (action === "approve") return "success";
        if (action === "request_changes") return "warning";
        if (action === "reject") return "danger";
        return "secondary";
    },
    historical_manual_review_sections() {
        const manual_item = this.active_historical_manual_item();
        if (!manual_item) return [];
        const source_sections = build_historical_manual_review_sections(manual_item, this.case_form_sections);
        const field_run_by_question = build_field_run_by_question(this.historical_manual_field_runs());
        return source_sections.map((form_section) => decorate_review_form_section(form_section, field_run_by_question)).filter((form_section) => Array.isArray(form_section?.sections) && form_section.sections.length);
    },
    historical_verification_check_expanded(item = {}) {
        return this.expanded_historical_verification_keys.includes(String(item?.id || ""));
    },
    toggle_historical_verification_check_expanded(item = {}) {
        const target_key = String(item?.id || "");
        if (!target_key) return;
        this.expanded_historical_verification_keys = this.historical_verification_check_expanded(item)
            ? this.expanded_historical_verification_keys.filter((key) => key !== target_key)
            : [...this.expanded_historical_verification_keys, target_key];
    },
    format_detail_value(value) { return manageVerificationSupportMethods.format_detail_value.call(this, value); },
    format_match_status(value = "") { return manageVerificationSupportMethods.format_match_status.call(this, value); },
    normalize_match_status(value = "") { return manageVerificationSupportMethods.normalize_match_status.call(this, value); },
    status_badge_class(tone) { return manageVerificationSupportMethods.status_badge_class.call(this, tone); },
    evidence_icon_class(tone) { return manageVerificationSupportMethods.evidence_icon_class.call(this, tone); },
    get_check_result_data(item = {}) { return manageVerificationResultMethods.get_check_result_data.call(this, item); },
    get_check_field_results(item = {}) { return manageVerificationResultMethods.get_check_field_results.call(this, item); },
    get_automatic_result_summary(item = {}) { return manageVerificationResultMethods.get_automatic_result_summary.call(this, item); },
    get_check_result_detail_rows(item = {}) { return manageVerificationResultMethods.get_check_result_detail_rows.call(this, item); },
    get_expected_verification_fields(item = {}) { return manageVerificationResultMethods.get_expected_verification_fields.call(this, item); },
    get_check_passed_chips(item = {}) { return manageVerificationResultMethods.get_check_passed_chips.call(this, item); },
    get_check_history(item = {}) { return manageVerificationResultMethods.get_check_history.call(this, item); },
    get_historical_status_message(tab = this.active_historical_gate_tab) {
        const gate_id = String(tab?.gate_id || "").trim().toLowerCase();
        if (gate_id === "draft") return "Application drafted.";
        if (gate_id === "submitted") return "Application submitted.";
        if (gate_id === "approved") return "Application approved.";
        if (gate_id === "rejected") return "Application rejected.";
        return `${tab?.label || "Gate"} completed.`;
    },
    historical_manual_secondary_tabs() { return [{ id: "manual-review-checklist", label: "1. Manual Review", active: true }, { id: "applicant-information", label: "2. Applicant Information" }, { id: "documents", label: "3. Documents" }, { id: "verification-results", label: "4. Verification Results" }]; },
    historical_risk_secondary_tabs() { return [{ id: "risk-review", label: "1. Risk Review", active: true }, { id: "history", label: "2. History" }, { id: "application", label: "3. Applicant Information" }]; },
    historical_manual_has_preview(item = {}) { return [".pdf", ".png", ".jpg", ".jpeg", ".doc", ".docx"].some((extension) => String(item?.answer || "").toLowerCase().includes(extension)); },
    get_historical_risk_factor_items() {
        const case_item = this.selected_case || {};
        const normalized_answers = build_historical_questionnaire_answers(case_item);
        const answer_map = new Map(normalized_answers.map((item) => [Number(item.id || 0), item]));
        const questions = this.historical_risk_questions.length ? this.historical_risk_questions : get_historical_risk_question_definitions(case_item);
        const submitted_answers = this.historical_risk_submitted?.answers && typeof this.historical_risk_submitted.answers === "object" ? this.historical_risk_submitted.answers : {};
        const weight_lookup = build_historical_risk_weight_lookup(this.historical_risk_weights);
        if (!questions.length) return get_historical_risk_fallback_items(case_item);

        return questions.map((question, index) => {
            const question_id = Number(question?.question_id || question?.id || index + 1);
            const answer_item =answer_map.get(question_id) || Reflect.get(normalized_answers, index) || {};            const submitted_value = Reflect.get(submitted_answers, String(question_id)) ?? Reflect.get(submitted_answers, question_id);
            const selected_answers = normalize_answer_values(submitted_value !== undefined ? submitted_value : answer_item?.selected_answer);
            const choices = this.historical_risk_weights.length ? build_historical_risk_choice_rows(question_id, this.historical_risk_weights, selected_answers) : get_historical_risk_choice_rows(question, selected_answers);
            const selected_score = this.historical_risk_weights.length ? selected_answers.reduce((total, answer) => total + (weight_lookup.get(`${question_id}:${answer}`) || 0), 0) : choices.filter((choice) => choice.is_selected).reduce((total, choice) => total + Number(choice.weight || 0), 0);
            const max_score = this.historical_risk_weights.length ? get_historical_risk_weight_max(question_id, this.historical_risk_weights) : get_historical_risk_max_score(question);
            return { id: `historical-risk-${question_id}`, label: get_first_text([question?.question_text, question?.text, question?.label], answer_item?.label || "Risk question"), selected_answer: selected_answers.join(", ") || answer_item?.selected_answer || "Not answered", selected_score, max_score, comparison_percent: max_score > 0 ? Math.round((selected_score / max_score) * 100) : 0, choices };
        });
    },
    historical_risk_total_possible_weight() { return this.get_historical_risk_factor_items().reduce((total, item) => total + Number(item.max_score || 0), 0); },
    historical_risk_score_value() {
        const factor_total = this.get_historical_risk_factor_items().reduce((total, item) => total + Number(item.selected_score || 0), 0);
        return factor_total > 0 ? factor_total : Number(this.selected_case?.risk_score || this.selected_case?.risk_result?.total_score || this.selected_case?.threshold_assessment?.score || 0);
    },
    historical_risk_score_percent() {
        const total_possible = this.historical_risk_total_possible_weight();
        return total_possible ? Math.round((this.historical_risk_score_value() / total_possible) * 100) : 0;
    },
    historical_risk_summary_value(label = "", fallback = "-") {
        const normalized_label = String(label || "").trim().toLowerCase();
        const case_item = this.selected_case || {};
        if (normalized_label === "risk level") return get_first_text([case_item?.risk, case_item?.risk_level, case_item?.risk_result?.risk_level], fallback);
        if (normalized_label === "auto decision") return get_first_text([case_item?.risk_result?.result, case_item?.risk_result?.status, case_item?.threshold_assessment?.result], fallback);
        if (normalized_label === "reviewer action") {
            const risk_level = this.historical_risk_summary_value("risk level", "").toLowerCase();
            if (risk_level.includes("high")) return "Review";
            if (risk_level.includes("medium")) return "Monitor";
            return "Auto-proceed";
        }
        return fallback;
    },
    historical_risk_review_guidance() {
        const action = this.historical_risk_summary_value("reviewer action", "");
        if (action === "Auto-proceed") return "Risk is acceptable. Confirm the evidence and advance the stage when ready.";
        if (action === "Monitor") return "Risk needs monitoring. Review the answers and request information if evidence is incomplete.";
        return "Review the risk evidence, then decide whether the application can move forward.";
    },
    historical_risk_badge_class() {
        const level = this.historical_risk_summary_value("risk level", "").toLowerCase();
        if (level.includes("high")) return "bg-danger-subtle text-danger-emphasis";
        if (level.includes("medium")) return "bg-warning-subtle text-warning-emphasis";
        return "bg-success-subtle text-success-emphasis";
    },
    get_historical_risk_summary_rows() {
        const case_item = this.selected_case || {};
        return [{ label: "Risk level", value: case_item?.risk || case_item?.risk_level || "Not assessed" }, { label: "Risk score", value: case_item?.risk_score || case_item?.risk_result?.total_score || "-" }, { label: "Result", value: case_item?.risk_result?.result || case_item?.risk_result?.status || "-" }, { label: "Stage", value: this.active_historical_gate_tab?.label || "Risk review" }];
    },
    async load_historical_gate_contexts(case_item = {}) {
        reset_historical_gate_state(this);
        if (!case_item || !this.historical_gate_tabs.length) return;

        const entity_profile_id = Number(case_item?.entity_profile_id || case_item?.profile?.id || 0);
        const forms = get_case_forms(case_item);
        const entity_type_id = get_case_entity_type_id(case_item, case_item);
        const binding_options = build_binding_options(case_item);
        const load_results = await Promise.all(build_load_promises(entity_profile_id, entity_type_id, binding_options, forms));
        const manual_binding_payload = apply_case_manual_binding_fallback(load_results[1], case_item);
        const fetched_data = [load_results[0], manual_binding_payload, ...load_results.slice(2)];
        const manual_mapping_by_link = await build_manual_mapping_by_link(manual_binding_payload?.manual_bindings);
        const checks = await enrich_manual_checks_with_run_mappings(build_case_detail_checks(case_item, forms, fetched_data, manual_mapping_by_link));
        const effective_manual_checks = await build_effective_manual_checks(case_item, forms, fetched_data, manual_mapping_by_link);
        this.historical_gate_checks_by_gate = build_checks_by_gate(this.historical_gate_tabs, checks, effective_manual_checks);
        await load_historical_risk_context(this, case_item);
    }
};
